import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { usePersistentState } from '../../lib/usePersistentState'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
FaArrowLeft, FaSignOutAlt, FaPlus, FaTrash, FaSearch,
FaFileInvoiceDollar, FaListUl, FaEye, FaEdit, FaChevronDown, FaChevronUp, FaChevronRight,
    FaCheckCircle, FaTimesCircle,
    FaClock, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaHashtag, FaPhone,
    FaHistory, FaCloudUploadAlt, FaCalculator, FaGripVertical, FaStickyNote, FaTags,
    FaMoneyBillWave, FaCopy, FaGlobe, FaSave, FaEyeSlash, FaEnvelope,
    FaHardHat, FaDraftingCompass, FaSync, FaExpand, FaBuilding, FaSpinner,
    FaStar, FaRegStar
} from 'react-icons/fa'
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core'
import {
    SortableContext, useSortable, verticalListSortingStrategy, arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CATALOG } from '../../lib/estimateData'
import { useCatalogItems } from '../../lib/catalogItems'
import { useCategories, getCatColor } from '../../lib/categories'
import EstimatePreview from './estimate/EstimatePreview'
import CatalogEditorTab from './estimate/CatalogEditorTab'
import CategoriesSubServicesTab from './estimate/CategoriesSubServicesTab'
import { saveDraft, loadDraft, deleteDraft } from '../../lib/offlineStore'
import { fetchAddressSuggestions, getPlaceDetails, newSessionToken } from '../../lib/googlePlaces'
import { useRole } from '../../hooks/useRole'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import { fetchAllProjects, fetchItemById, updateItemName, updateSimpleColumnValue, updateColumnValue } from '../../lib/monday'
import { searchGHLContact, updateGHLContact, getContactOpportunities, updateGHLOpportunity, GHL_CONFIG } from '../../lib/ghl'
import { VENTAS_PIPELINE_ID, CONSTRUCCION_PIPELINE_ID } from '../../lib/constructionMapping'
import { useEstimates, useArchivedEstimates, saveEstimate, archiveEstimate, markEstimateStatus, updateAddressDetails, resolveLines, cloneEstimate, newEstimateNumber } from '../../lib/estimates'
import { usePriceGroups } from '../../lib/priceGroups'
import PriceGroupsManager from './estimate/PriceGroupsManager'
import ArchEstimateBuilder from './ArchEstimateBuilder'
import { useConSchedulePresets } from '../../lib/conSchedulePresets'

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const pct = (n) => (Number(n || 0) * 100).toFixed(1) + '%'

// Date ↔ input[type=date] converters
const _LEGACY_MONTHS = { January:'01',February:'02',March:'03',April:'04',May:'05',June:'06',July:'07',August:'08',September:'09',October:'10',November:'11',December:'12' }
function toInputDate(str) {
    if (!str) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
    // Legacy "Month DD, YYYY" stored before ISO migration — parse without Date constructor (Safari-safe)
    const m = str.match(/^(\w+)\s+(\d{1,2}),\s+(\d{4})$/)
    if (m) return `${m[3]}-${_LEGACY_MONTHS[m[1]] ?? '01'}-${String(m[2]).padStart(2,'0')}`
    return ''
}
function fromInputDate(iso) {
    return iso || ''
}

const EMPTY_ESTIMATE = () => ({
    number: newEstimateNumber(),
    date: new Date().toISOString().split('T')[0],
    client: '',
    address: '',
    addressDetails: { address: '', lat: null, lng: null, zipCode: '' },
    subtitle: '',
    validDays: 10,
    notes: '',
    lines: [],
    paymentSchedule: [],
    status: 'draft',
})

function getStandardPaymentSchedule(dbPresets = []) {
    const dbDefault = dbPresets.find(p => p.is_default)
    if (dbDefault) return dbDefault.rows.map(r => ({ label: r.label || '', pct: r.pct }))
    const dbStandard = dbPresets.find(p => p.name === 'Standard (30/40/20/10)')
    if (dbStandard) return dbStandard.rows.map(r => ({ label: r.label || '', pct: r.pct }))
    return []
}

// ── Client autocomplete (Monday — Estimados 2026 board) ────────────────────
const MONDAY_CLIENT_NAME_COL = 'text_mkt9adtr'
const MONDAY_ADDRESS_COL     = 'location_mkt69c7k'
const MONDAY_EMAIL_COL       = 'text_mkt9qv82'
const MONDAY_PHONE_COL       = 'phone_mkt9n841'
const MONDAY_CONSTRUCTION_BOARD_ID = '18157237865'

function getMonColText(item, colId) {
    return item.column_values?.find(c => c.id === colId)?.text || ''
}

function cleanMondayAddress(raw) {
    if (!raw) return ''
    const idx = raw.indexOf('|')
    return idx !== -1 ? raw.slice(idx + 1).trim() : raw
}

// Backfill clientPhone/clientEmail (and mondayItemId when uniquely matched) from
// Monday for estimates saved before contact capture existed. Persists to DB.
async function enrichEstimateContact(base, session) {
    const ad = base?.addressDetails || {}
    if (ad.clientPhone && ad.clientEmail && ad.mondayItemId) return base

    let item = null
    if (ad.mondayItemId) {
        item = await fetchItemById(ad.mondayItemId, session).catch(() => null)
    }
    if (!item && base.client) {
        const norm = s => (s || '').trim().toLowerCase()
        const raw = await fetchAllProjects([MONDAY_CONSTRUCTION_BOARD_ID], session).catch(() => [])
        const items = raw.map(w => w.item || w)
        const byName = i => norm(getMonColText(i, MONDAY_CLIENT_NAME_COL)) === norm(base.client)
        const byAddr = i => ad.address && norm(cleanMondayAddress(getMonColText(i, MONDAY_ADDRESS_COL) || i.name || '')) === norm(ad.address)
        let matches = items.filter(byName)
        if (matches.length > 1 && ad.address) matches = matches.filter(byAddr)
        if (matches.length !== 1) matches = items.filter(byAddr)
        if (matches.length === 1) item = matches[0]
    }
    if (!item) return base

    const merged = { ...ad }
    if (!merged.clientEmail) merged.clientEmail = getMonColText(item, MONDAY_EMAIL_COL)
    if (!merged.clientPhone) merged.clientPhone = getMonColText(item, MONDAY_PHONE_COL)
    if (!merged.mondayItemId) {
        merged.mondayItemId  = String(item.id)
        merged.mondayBoardId = merged.mondayBoardId || MONDAY_CONSTRUCTION_BOARD_ID
    }
    if (merged.clientEmail === ad.clientEmail && merged.clientPhone === ad.clientPhone && merged.mondayItemId === ad.mondayItemId) return base

    if (base.id) updateAddressDetails(base.id, merged).catch(() => {})
    return { ...base, addressDetails: merged }
}

function ClientAutocomplete({ value, session, onChange }) {
    const { t } = useTranslation()
    const [inputValue,  setInputValue]  = useState(value || '')
    const [suggestions, setSuggestions] = useState([])
    const [allItems,    setAllItems]    = useState(null)
    const [loading,     setLoading]     = useState(false)
    const [highlighted, setHighlighted] = useState(-1)
    const [dropPos,     setDropPos]     = useState(null)
    const debounceRef   = useRef(null)
    const userTypingRef = useRef(false)
    const inputRef      = useRef(null)

    const computeDropPos = useCallback(() => {
        if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
    }, [])

    useEffect(() => {
        if (!userTypingRef.current) setInputValue(value || '')
    }, [value])

    const ensureLoaded = useCallback(async () => {
        if (allItems !== null) return
        setLoading(true)
        try {
            const raw = await fetchAllProjects(['18157237865'], session)
            setAllItems(raw.map(({ item }) => item))
        } catch { setAllItems([]) }
        finally { setLoading(false) }
    }, [allItems, session])

    const refreshClients = useCallback(async () => {
        setLoading(true)
        try {
            const raw = await fetchAllProjects(['18157237865'], session)
            setAllItems(raw.map(({ item }) => item))
        } catch { setAllItems([]) }
        finally { setLoading(false) }
    }, [session])

    const runFilter = useCallback((text, items) => {
        const q = text.trim().toLowerCase()
        if (!q || q.length < 2) { setSuggestions([]); return }
        setSuggestions(
            (items || []).filter(item =>
                getMonColText(item, MONDAY_CLIENT_NAME_COL).toLowerCase().includes(q)
            ).slice(0, 8)
        )
    }, [])

    const handleInput = (text) => {
        setInputValue(text)
        userTypingRef.current = true
        setHighlighted(-1)
        computeDropPos()
        onChange({ client: text })
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runFilter(text, allItems), 300)
    }

    const handleSelect = (item) => {
        const clientName = getMonColText(item, MONDAY_CLIENT_NAME_COL)
        const address    = cleanMondayAddress(getMonColText(item, MONDAY_ADDRESS_COL) || item.name || '')
        const email      = getMonColText(item, MONDAY_EMAIL_COL)
        const phone      = getMonColText(item, MONDAY_PHONE_COL)
        setInputValue(clientName)
        setSuggestions([])
        userTypingRef.current = false
        onChange({
            client: clientName,
            address,
            addressDetails: {
                address, lat: null, lng: null, zipCode: '',
                mondayItemId:    String(item.id),
                mondayBoardId:   MONDAY_CONSTRUCTION_BOARD_ID,
                originalClient:  clientName,
                originalAddress: address,
                clientEmail:     email,
                clientPhone:     phone,
            },
        })
    }

    const handleKeyDown = (e) => {
        if (!suggestions.length) return
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, suggestions.length - 1)) }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
        if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); handleSelect(suggestions[highlighted]) }
        if (e.key === 'Escape') { setSuggestions([]) }
    }

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    return (
        <div className="relative">
            <FaUser className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={11} />
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => handleInput(e.target.value)}
                onFocus={async () => {
                    computeDropPos()
                    await ensureLoaded()
                    runFilter(inputValue, allItems)
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setSuggestions([]), 200)}
                placeholder={t('admin.estimator.client_placeholder')}
                autoComplete="off"
                className="py-2 pr-8 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            {loading && (
                <span className="absolute right-3 top-1/2 text-xs text-gray-400 -translate-y-1/2">{t('admin.estimator.loading')}</span>
            )}
            {!loading && allItems !== null && (
                <button
                    type="button"
                    onMouseDown={e => { e.preventDefault(); refreshClients() }}
                    className="absolute right-2 top-1/2 p-1 text-gray-300 transition-colors -translate-y-1/2 hover:text-gray-500"
                    title={t('admin.estimator.refresh_clients')}
                >
                    <FaHistory size={9} />
                </button>
            )}
            {suggestions.length > 0 && dropPos && (
                <ul style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }} className="z-[9999] overflow-hidden overflow-y-auto max-h-60 bg-white rounded-xl border border-gray-200 shadow-xl">
                    {suggestions.map((item, idx) => {
                        const name    = getMonColText(item, MONDAY_CLIENT_NAME_COL)
                        const address = cleanMondayAddress(getMonColText(item, MONDAY_ADDRESS_COL) || item.name || '')
                        return (
                            <li
                                key={item.id}
                                onMouseDown={e => { e.preventDefault(); handleSelect(item) }}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${highlighted === idx ? 'bg-primary/5' : 'hover:bg-primary/5'}`}
                            >
                                <p className="font-medium text-gray-800">{name}</p>
                                {address && (
                                    <p className="flex items-center gap-1 mt-0.5 text-xs text-gray-400 truncate">
                                        <FaMapMarkerAlt size={9} className="shrink-0" />{address}
                                    </p>
                                )}
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

// ── Address autocomplete (Google Places — shared lib) ────────────────────────
function AddressAutocomplete({ value, onChange }) {
  const [inputValue, setInputValue] = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [dropPos, setDropPos] = useState(null)
  const debounceRef = useRef(null)
  const userTypingRef = useRef(false)
  const inputRef = useRef(null)

  const computeDropPos = useCallback(() => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect()
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
  }, [])

  useEffect(() => {
    if (!userTypingRef.current) {
      setInputValue(value || '') // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [value])

  const handleInput = (text) => {
    setInputValue(text)
    onChange({ address: text, lat: null, lng: null, zipCode: '' })
    userTypingRef.current = true
    computeDropPos()

    if (!text.trim()) {
      setSuggestions([])
      return
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await fetchAddressSuggestions(text.trim())
        setSuggestions(results)
      } catch {
        setSuggestions([])
      }
    }, 300)
  }

  const handleSelect = async (suggestion) => {
    const details = suggestion.placeId ? await getPlaceDetails(suggestion.placeId) : null
    const address = details?.address || suggestion.value || suggestion.label
    const result = {
      address,
      lat: details?.lat ?? null,
      lng: details?.lng ?? null,
      zipCode: details?.zipCode ?? '',
    }
    setInputValue(address)
    onChange(result)
    userTypingRef.current = false
    setSuggestions([])
    newSessionToken()
  }

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    return (
        <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={11} />
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => handleInput(e.target.value)}
                onFocus={() => { computeDropPos(); newSessionToken() }}
                placeholder="123 Main St, Miami, FL 33101"
                autoComplete="off"
                className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            {suggestions.length > 0 && dropPos && (
                <ul style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }} className="z-[9999] overflow-hidden bg-white rounded-xl border border-gray-200 shadow-xl">
                    {suggestions.map(s => (
                        <li
                            key={s.id}
                            onMouseDown={e => { e.preventDefault(); handleSelect(s) }}
                            className="px-4 py-2.5 text-sm text-gray-800 cursor-pointer hover:bg-primary/5 transition-colors"
                        >
                            <p className="font-medium">{s.label}</p>
                            {s.secondary && <p className="text-xs text-gray-400">{s.secondary}</p>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

// ── CATALOG TAB ───────────────────────────────────────────────────────────────
function CatalogTab({ onAddToEstimate, session }) {
    const { t } = useTranslation()
    const { items: dbItems, loading: catalogLoading } = useCatalogItems(session)
    const { categories } = useCategories()
    const catalogSource = catalogLoading ? CATALOG : dbItems
    const [search, setSearch]       = useState('')
    const [catFilter, setCatFilter] = useState(null)
    const [expanded, setExpanded]   = useState({})

  const catalogCats = useMemo(() =>
    [...new Set(catalogSource.map(i => i.cat).filter(Boolean))].sort(),
    [catalogSource]
  )

  const filtered = useMemo(() => {
    const q = search.trim();
    let regex;
    try { regex = q ? new RegExp(q, 'i') : null } catch { regex = null }
    return catalogSource
      .filter((item, idx, arr) => arr.findIndex(x => x.id === item.id) === idx)
      .filter(item => {
        const matchesCat = !catFilter || item.cat?.toUpperCase() === catFilter.toUpperCase();
        const matchesSearch = !q || (regex ? regex.test(item.name) : item.name.toLowerCase().includes(q.toLowerCase()));
        return matchesCat && matchesSearch;
      });
  }, [catalogSource, search, catFilter])

    const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }))

    return (
        <div>
            {/* Search + category filters */}
            <div className="flex flex-col gap-2 mb-4 lg:flex-row">
    <div className="relative flex-1">
      <FaSearch className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" size={13} />
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={t('admin.estimator.search_catalog')}
        className="py-2 pr-3 pl-9 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      {search && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
          {t('admin.estimator.search_results', { found: filtered.length, total: catalogSource.length })}
        </span>
      )}
    </div>
                <div className="flex flex-nowrap overflow-x-auto gap-1.5 lg:flex-wrap lg:overflow-visible">
                    <button
                        onClick={() => setCatFilter(null)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!catFilter ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    >{t('admin.estimator.filter_all')} ({catalogSource.length})</button>
                    {catalogCats.map(catName => {
                        const col = getCatColor(categories, catName)
                        return (
                            <button
                                key={catName}
                                onClick={() => setCatFilter(catFilter === catName ? null : catName)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${catFilter === catName ? col.badge + ' shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                            >{catName} ({catalogSource.filter(i=>i.cat===catName).length})</button>
                        )
                    })}
                </div>
            </div>

            {catFilter && (
                <div className="mb-2 flex items-center gap-2 text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                    <span>{t('admin.estimator.filtering_by')}</span>
                    <span className="font-bold text-primary">{catFilter} ({t('admin.estimator.items_count', { count: filtered.length })})</span>
                    <button onClick={() => setCatFilter(null)} className="ml-1 text-gray-300 transition-colors hover:text-red-500">{t('admin.estimator.clear_filter')}</button>
                </div>
            )}

            {/* Product list */}
            <div className="space-y-1.5">
                {filtered.map(item => {
                    const col = getCatColor(categories, item.cat)
                    const open = expanded[item.id]
                    const margin = item.price > 0 ? ((item.price - item.cost) / item.price * 100).toFixed(1) : 0
                    return (
                        <div key={item.id} className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex gap-3 items-center px-4 py-3">
                                <div className={`w-2 h-2 rounded-full shrink-0 ${col.dot}`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                                    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${col.badge}`}>{item.cat}</span>
                                        <span>${fmt(item.price)} / {item.unit}</span>
                                        <span className="font-semibold text-emerald-600">{t('admin.estimator.margin_pct', { pct: margin })}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 items-center shrink-0">
                                    {(item.inc?.length > 0 || item.exc?.length > 0) && (
                                        <button
                                            onClick={() => toggle(item.id)}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                            title={t('admin.estimator.view_scope')}
                                        >
                                            {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => onAddToEstimate(item)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                                    >
                                        <FaPlus size={10} /> {t('admin.estimator.add')}
                                    </button>
                                </div>
                            </div>
                            {/* Scope detail */}
                            <AnimatePresence>
                                {open && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden"
                                    >
                                        <div className={`px-4 pb-3 pt-1 ${col.bg} border-t border-gray-100 grid sm:grid-cols-2 gap-3`}>
                                            {item.inc?.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-1 flex items-center gap-1"><FaCheckCircle size={9} /> {t('admin.estimator.included')}</p>
                                                    <ul className="space-y-0.5">
                                                        {item.inc.map((b,i) => <li key={i} className="text-xs text-gray-600 flex gap-1.5"><span className="text-emerald-500 mt-0.5 shrink-0">✓</span>{b}</li>)}
                                                    </ul>
                                                </div>
                                            )}
                                            {item.exc?.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-red-600 uppercase tracking-wide mb-1 flex items-center gap-1"><FaTimesCircle size={9} /> {t('admin.estimator.not_included')}</p>
                                                    <ul className="space-y-0.5">
                                                        {item.exc.map((b,i) => <li key={i} className="text-xs text-gray-500 flex gap-1.5"><span className="text-red-400 mt-0.5 shrink-0">✕</span>{b}</li>)}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )
                })}
                {filtered.length === 0 && (
                    <div className="py-12 text-sm text-center text-gray-400">{t('admin.estimator.no_items_found')}</div>
                )}
            </div>
        </div>
    )
}

// ── LINE ITEM ROW ─────────────────────────────────────────────────────────────
function LineItemRow({ line, index, onChange, onRemove, dragHandleProps, dragStyle, categories }) {
    const col = getCatColor(categories || [], line.cat)
    const total = (line.qty || 0) * (line.price || 0)
    const totalCostRow = (line.qty || 0) * (line.cost || 0)
    const rowMargin = total > 0 ? (total - totalCostRow) / total : 0

    return (
        <tr className="border-b border-gray-100 transition-colors group hover:bg-gray-50" style={dragStyle}>
            <td className="px-3 py-2 text-xs text-center text-gray-400">
                <span {...dragHandleProps} className="inline-block mr-1 text-gray-300 cursor-grab active:cursor-grabbing touch-none"><FaGripVertical size={11} /></span>
                {index + 1}
            </td>
            <td className="px-3 py-2">
                <div className="flex gap-2 items-start">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${col.dot}`} />
                    <div className="min-w-0">
                        <p className="text-sm leading-tight text-gray-800">{line.name}</p>
                        <span className={`inline-flex px-1.5 py-0.5 mt-0.5 rounded-full text-[10px] font-semibold border ${col.badge}`}>{line.cat}</span>
                    </div>
                </div>
            </td>
            <td className="px-3 py-2 text-xs text-center text-gray-500 whitespace-nowrap">{line.unit}</td>
            <td className="px-3 py-2">
                <input
                    type="number"
                    value={line.qty}
                    min="0"
                    step="0.01"
                    onChange={e => onChange(index, 'qty', parseFloat(e.target.value) || 0)}
                    className="px-2 py-1 w-20 text-sm text-center rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
            </td>
            <td className="px-3 py-2">
                <div className="relative w-24">
                    <span className="absolute left-2 top-1/2 text-xs text-gray-400 -translate-y-1/2">$</span>
                    <input
                        type="number"
                        value={line.price}
                        min="0"
                        step="0.01"
                        onChange={e => onChange(index, 'price', parseFloat(e.target.value) || 0)}
                        className="py-1 pr-2 pl-5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
            </td>
            <td className="px-3 py-2 text-sm font-semibold text-right text-gray-800">${fmt(total)}</td>
            <td className={`px-3 py-2 text-xs font-semibold text-right ${rowMargin >= 0.35 ? 'text-emerald-600' : 'text-amber-600'}`}>{(rowMargin * 100).toFixed(1)}%</td>
            <td className="px-3 py-2">
                <button
                    onClick={() => onRemove(index)}
                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                >
                    <FaTrash size={11} />
                </button>
            </td>
        </tr>
    )
}

// ── SORTABLE WRAPPER ──────────────────────────────────────────────────────────
function SortableLineItemRow({ id, ...props }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 'auto',
        position: isDragging ? 'relative' : 'static',
    }
    return (
        <LineItemRow
            {...props}
            dragHandleProps={{ ref: setNodeRef, ...attributes, ...listeners }}
            dragStyle={style}
        />
    )
}


// ── PAYMENT SCHEDULE EDITOR ───────────────────────────────────────────────────
function PaymentScheduleEditor({ schedule, totalVenta, onChange, dbPresets = [] }) {
    const { t } = useTranslation()
    const total  = schedule.reduce((s, r) => s + (Number(r.pct) || 0), 0)
    const isValid = total === 100
    const canAdd  = schedule.length < 10
    const canDel  = schedule.length > 1

    const updateRow = (idx, field, val) =>
        onChange(schedule.map((r, i) => i === idx ? { ...r, [field]: val } : r))

    const deleteRow = (idx) =>
        onChange(schedule.filter((_, i) => i !== idx))

    const applyPreset = (rows) =>
        onChange(rows.map(r => ({ ...r })))

    const activePresetName = useMemo(() => {
        if (schedule.length === 0) return null
        for (const preset of dbPresets) {
            const rows = preset.rows || []
            if (rows.length !== schedule.length) continue
            const match = rows.every((pr, i) => {
                const cr = schedule[i]
                return (pr.label || '') === (cr.label?.trim() || '') &&
                       (Number(pr.pct) || 0) === (Number(cr.pct) || 0)
            })
            if (match) return preset.name
        }
        return null
    }, [schedule, dbPresets])

    return (
        <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
                <h3 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                    <FaMoneyBillWave size={12} /> {t('admin.estimator.payment_schedule')}
                </h3>
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border transition-all ${
                    isValid
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-600 border-red-200'
                }`}>
                    {isValid ? <FaCheckCircle size={9} /> : '⚠'} {total}% / 100%
                </span>
            </div>

            <div className="px-4 pt-3 pb-1">
                {/* Preset strip */}
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide shrink-0">{t('admin.estimator.presets')}</span>
                    {dbPresets.map(p => {
                        const isDefault = !!p.is_default
                        const isActive = activePresetName === p.name
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => applyPreset((p.rows || []).map(r => ({ label: r.label || '', pct: r.pct })))}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all flex items-center gap-1 ${
                                    isActive
                                        ? 'bg-primary text-white border-primary shadow-sm'
                                        : isDefault
                                            ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                            : 'border-gray-200 bg-white text-gray-600 hover:bg-primary/5 hover:border-primary/30'
                                }`}
                            >
                                {isDefault && !isActive && <FaStar size={7} className="text-amber-500" />}
                                {p.name}
                            </button>
                        )
                    })}
                </div>

                {/* Column headers */}
                <div className="flex items-center gap-2 mb-1 px-0.5">
                    <span className="flex-1 text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{t('admin.estimator.milestone_label')}</span>
                    <span className="w-16 text-center text-[10px] text-gray-400 uppercase tracking-wide font-semibold shrink-0">%</span>
                    <span className="w-20 text-right text-[10px] text-gray-400 uppercase tracking-wide font-semibold shrink-0">{t('admin.estimator.col_amount')}</span>
                    <span className="w-7 shrink-0" />
                </div>

                {/* Rows */}
                <div className="space-y-1.5 mb-2">
                    {schedule.map((row, idx) => {
                        const amount = totalVenta > 0 ? totalVenta * ((Number(row.pct) || 0) / 100) : null
                        return (
                            <div key={idx} className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={row.label}
                                    onChange={e => updateRow(idx, 'label', e.target.value)}
                                    placeholder={t('admin.estimator.payment_placeholder', { n: idx + 1 })}
                                    maxLength={120}
                                    className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 min-w-0"
                                />
                                <div className="relative w-16 shrink-0">
                                    <input
                                        type="number"
                                        value={row.pct}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onChange={e => updateRow(idx, 'pct', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                        className={`w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                                            total > 100 ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                        }`}
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                </div>
                                <span className="w-20 text-xs font-semibold tabular-nums text-right text-gray-500 shrink-0">
                                    {amount !== null ? `$${fmt(amount)}` : '—'}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => deleteRow(idx)}
                                    disabled={!canDel}
                                    className="w-7 flex justify-center p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                    title={t('admin.estimator.remove')}
                                >
                                    <FaTimesCircle size={11} />
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                <button
                    type="button"
                    onClick={() => onChange([...schedule, { label: '', pct: 0 }])}
                    disabled={!canAdd}
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-40 disabled:pointer-events-none transition-all"
                >
                    <FaPlus size={9} /> {t('admin.estimator.add_installment')}
                </button>
                <span className={`text-xs font-bold ${isValid ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isValid ? t('admin.estimator.total_valid') : t('admin.estimator.total_invalid', { diff: `${total > 100 ? '+' : ''}${total - 100}` })}
                </span>
            </div>
        </div>
    )
}

// ── BUILDER TAB ───────────────────────────────────────────────────────────────
function BuilderTab({ estimate, onChange, session, conSchedulePresets = [], onPushToSources, isPushing }) {
    const { t } = useTranslation()
    const { categories } = useCategories()
    const { items: dbItems, loading: catalogLoading } = useCatalogItems()
    const { groups: priceGroups, getPriceForItem, getGroupForZip } = usePriceGroups()
    const catalogSource = catalogLoading ? CATALOG : dbItems
    const [search, setSearch] = useState('')
    const [catFilter, setCatFilter] = useState(null)
    const [showCatalogPicker, setShowCatalogPicker] = useState(false)
    const [notesOpen, setNotesOpen] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [activePriceGroupId, setActivePriceGroupId] = useState(null)
    const [pgAutoDetected, setPgAutoDetected] = useState(false)
    const [overrideMode, setOverrideMode] = useState(false)

    // Exit override mode when Monday item changes or is cleared
    useEffect(() => {
        setOverrideMode(false)
    }, [estimate.addressDetails?.mondayItemId])

    // Auto-detect price group from zip code
    useEffect(() => {
        const zip = estimate.addressDetails?.zipCode
        if (!zip || priceGroups.length === 0) return
        const matched = getGroupForZip(zip)
        setActivePriceGroupId(matched?.id || null)
        setPgAutoDetected(true)
    }, [estimate.addressDetails?.zipCode, priceGroups, getGroupForZip])

    // Detect corrected client/address from Monday originals
    const isCorrected = useMemo(() => {
        const ad = estimate.addressDetails || {}
        if (!ad.mondayItemId) return false
        return estimate.client !== ad.originalClient || estimate.address !== ad.originalAddress
    }, [estimate.client, estimate.address, estimate.addressDetails?.mondayItemId, estimate.addressDetails?.originalClient, estimate.addressDetails?.originalAddress])

  // Dynamically detect all categories from catalog items
  const catalogCats = useMemo(() =>
    [...new Set(catalogSource.map(i => i.cat).filter(Boolean))].sort(),
    [catalogSource]
  )

  const filteredCatalog = useMemo(() => {
    const q = search.trim()
    let regex
    try { regex = q ? new RegExp(q, 'i') : null } catch { regex = null }
    return catalogSource
      .filter((item, idx, arr) => arr.findIndex(x => x.id === item.id) === idx)
      .filter(item => {
        const matchesCat = !catFilter || item.cat?.toUpperCase() === catFilter.toUpperCase()
        const matchesSearch = !q || (regex ? regex.test(item.name) : item.name.toLowerCase().includes(q.toLowerCase()))
        return matchesCat && matchesSearch
      })
  }, [catalogSource, search, catFilter])

    const addLine = (item) => {
        const override = activePriceGroupId ? getPriceForItem(item.id, activePriceGroupId) : null
        onChange('lines', [...estimate.lines, {
            ...item,
            qty:   1,
            price: override?.price ?? item.price,
            cost:  override?.cost  ?? item.cost,
        }])
        setSearch('')
        setShowCatalogPicker(false)
    }

    const updateLine = (idx, field, val) => {
        const lines = [...estimate.lines]
        lines[idx] = { ...lines[idx], [field]: val }
        onChange('lines', lines)
    }

    const removeLine = (idx) => {
        onChange('lines', estimate.lines.filter((_, i) => i !== idx))
    }

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return
        const oldIndex = estimate.lines.findIndex((_, i) => `line-${i}` === active.id)
        const newIndex = estimate.lines.findIndex((_, i) => `line-${i}` === over.id)
        if (oldIndex !== -1 && newIndex !== -1) {
            onChange('lines', arrayMove(estimate.lines, oldIndex, newIndex))
        }
    }

    const TARGET_MARGIN = 0.35
    const totalVenta = estimate.lines.reduce((s, l) => s + (l.qty||0)*(l.price||0), 0)
    const totalCost  = estimate.lines.reduce((s, l) => s + (l.qty||0)*(l.cost||0), 0)
    const utilidad   = totalVenta - totalCost
    const margin     = totalVenta > 0 ? utilidad / totalVenta : 0
    const precioMin  = totalCost > 0 ? totalCost / (1 - TARGET_MARGIN) : 0
    const diferencia = precioMin - totalVenta

    return (
        <div className="space-y-4">
            {/* Client & Project Info */}
            <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-700"><FaUser size={12} /> {t('admin.estimator.client_info')}</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                    {/* Estimate # — readonly, auto-generated */}
                    <div>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500"><FaHashtag size={10} /> {t('admin.estimator.estimate_num')}</div>
                        <input
                            type="text"
                            value={estimate.number}
                            readOnly
                            title={t('admin.estimator.auto_generated')}
                            className="px-3 py-2 w-full font-mono text-xs text-gray-400 bg-gray-50 rounded-lg border border-gray-100 cursor-default select-all"
                        />
                    </div>

                    {/* Date picker */}
                    <div>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500"><FaCalendarAlt size={10} /> {t('admin.estimator.issue_date')}</div>
                        <input
                            type="date"
                            value={toInputDate(estimate.date)}
                            onChange={e => onChange('date', fromInputDate(e.target.value))}
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    {/* Client name — Monday autocomplete */}
                    <div className={overrideMode ? 'p-2 -m-2 rounded-lg ring-2 ring-amber-300' : ''}>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500">
                            <FaUser size={10} /> {t('admin.estimator.client_name')}
                            {overrideMode && isCorrected && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full">{t('admin.estimator.corrected')}</span>
                            )}
                        </div>
                        <ClientAutocomplete
                            value={estimate.client || ''}
                            session={session}
                            onChange={({ client, address, addressDetails }) => {
                                if (client !== undefined) onChange('client', client)
                                if (address !== undefined) onChange('address', address)
                                if (addressDetails !== undefined) onChange('addressDetails', addressDetails)
                            }}
                        />
                        {estimate.addressDetails?.mondayItemId && (
                            <button
                                type="button"
                                onClick={() => setOverrideMode(v => !v)}
                                className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${
                                    overrideMode
                                        ? 'text-amber-700 bg-amber-100 border border-amber-300 hover:bg-amber-200'
                                        : 'text-gray-500 bg-gray-50 border border-gray-200 hover:bg-gray-100'
                                }`}
                            >
                                <FaEdit size={10} />
                                {t('admin.estimator.override_mode')}
                            </button>
                        )}
                    </div>

                    {/* Client phone + email — autofilled by Monday autocomplete, editable for manual clients */}
                    <div>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500"><FaPhone size={10} /> {t('admin.estimator.client_phone')}</div>
                        <input
                            type="tel"
                            value={estimate.addressDetails?.clientPhone || ''}
                            onChange={e => onChange('addressDetails', { ...(estimate.addressDetails || {}), clientPhone: e.target.value })}
                            placeholder="(305) 555-1234"
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                    <div>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500"><FaEnvelope size={10} /> {t('admin.estimator.client_email')}</div>
                        <input
                            type="email"
                            value={estimate.addressDetails?.clientEmail || ''}
                            onChange={e => onChange('addressDetails', { ...(estimate.addressDetails || {}), clientEmail: e.target.value })}
                            placeholder="email@example.com"
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    {/* Valid for (days) */}
                    <div>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500"><FaClock size={10} /> {t('admin.estimator.valid_for')}</div>
                        <div className="relative">
                            <input
                                type="number"
                                min="1"
                                max="365"
                                value={estimate.validDays || 10}
                                onChange={e => onChange('validDays', parseInt(e.target.value) || 10)}
                                className="px-3 py-2 pr-12 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <span className="absolute right-3 top-1/2 text-xs text-gray-400 -translate-y-1/2 pointer-events-none">{t('admin.estimator.days')}</span>
                        </div>
                    </div>

                    {/* Project address — Google Places autocomplete */}
                    <div className={`sm:col-span-2 ${overrideMode ? 'p-2 -m-2 rounded-lg ring-2 ring-amber-300' : ''}`}>
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500">
                            <FaMapMarkerAlt size={10} /> {t('admin.estimator.project_address')}
                            {overrideMode && isCorrected && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full">{t('admin.estimator.corrected')}</span>
                            )}
                        </div>
                        <AddressAutocomplete
                            value={estimate.address || ''}
                            onChange={details => {
                              onChange('address', details.address);
                              onChange('addressDetails', { ...estimate.addressDetails, ...details });
                            }}
                        />
                        {overrideMode && (
                            <button
                                onClick={onPushToSources}
                                disabled={isPushing}
                                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-60 rounded-lg transition-colors"
                            >
                                {isPushing ? <FaSpinner className="animate-spin" size={12} /> : <FaSync size={12} />}
                                {t('admin.estimator.push_to_sources')}
                            </button>
                        )}
                    </div>

                    {/* Price Group selector */}
                    {priceGroups.length > 0 && (
                        <div className="sm:col-span-2">
                            <div className="flex gap-1 items-center mb-1 text-xs text-gray-500">
                                <FaMoneyBillWave size={10} /> {t('admin.estimator.price_group')}
                                {pgAutoDetected && activePriceGroupId && (
                                    <span className="ml-1 text-[10px] text-blue-500 font-semibold">{t('admin.estimator.pg_auto_detected')}</span>
                                )}
                            </div>
                            <select
                                value={activePriceGroupId || ''}
                                onChange={e => { setActivePriceGroupId(e.target.value || null); setPgAutoDetected(false) }}
                                className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            >
                                <option value="">{t('admin.estimator.pg_default')}</option>
                                {priceGroups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Scope subtitle */}
                    <div className="sm:col-span-2">
                        <div className="flex gap-1 items-center mb-1 text-xs text-gray-500">
                            <FaEdit size={10} /> {t('admin.estimator.scope_subtitle')}
                            <span className="text-gray-400 text-[10px] ml-1">{t('admin.estimator.optional')}</span>
                        </div>
                        <input
                            type="text"
                            value={estimate.subtitle || ''}
                            onChange={e => onChange('subtitle', e.target.value)}
                            placeholder={t('admin.estimator.subtitle_placeholder')}
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    {/* Additional notes */}
                    <div className="sm:col-span-2">
                        <div className="flex justify-between items-center mb-1">
                            <div className="flex gap-1 items-center text-xs text-gray-500">
                                <FaStickyNote size={10} /> {t('admin.estimator.additional_notes')}
                                <span className="text-gray-400 text-[10px] ml-1">{t('admin.estimator.optional_printed')}</span>
                            </div>
                            <button
                                onClick={() => setNotesOpen(true)}
                                title="Expand notes"
                                className="p-1 text-gray-400 rounded transition-colors hover:bg-gray-100 hover:text-gray-600"
                            >
                                <FaExpand size={10} />
                            </button>
                        </div>
                        <textarea
                            rows={3}
                            value={estimate.notes || ''}
                            onChange={e => onChange('notes', e.target.value)}
                            placeholder={t('admin.estimator.notes_placeholder')}
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    {/* Notes modal */}
                    <AnimatePresence>
                        {notesOpen && (
                            <motion.div
                                key="notes-modal-cons"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/40"
                                onClick={e => { if (e.target === e.currentTarget) setNotesOpen(false) }}
                            >
                                <motion.div
                                    initial={{ scale: 0.96, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.96, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="flex overflow-hidden flex-col w-full max-w-2xl bg-white rounded-2xl shadow-2xl"
                                >
                                    <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100">
                                        <h3 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                                            <FaStickyNote size={12} className="text-primary" />
                                            {t('admin.estimator.additional_notes')}
                                            <span className="text-[10px] font-normal text-gray-400 ml-1">{t('admin.estimator.optional_printed')}</span>
                                        </h3>
                                        <button onClick={() => setNotesOpen(false)} className="p-1.5 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors">
                                            <FaTimesCircle size={12} />
                                        </button>
                                    </div>
                                    <textarea
                                        autoFocus
                                        rows={18}
                                        value={estimate.notes || ''}
                                        onChange={e => onChange('notes', e.target.value)}
                                        placeholder={t('admin.estimator.notes_placeholder')}
                                        className="px-5 py-4 w-full text-sm border-0 resize-none focus:outline-none"
                                    />
                                    <div className="flex justify-end px-5 py-3 bg-gray-50 border-t border-gray-100">
                                        <button
                                            onClick={() => setNotesOpen(false)}
                                            className="px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors bg-primary hover:bg-primary/90"
                                        >
                                            {t('admin.estimator.notes_done')}
                                        </button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                </div>
            </div>

            {/* Line items table */}
            <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
                    <h3 className="flex gap-2 items-center text-sm font-bold text-gray-700"><FaListUl size={12} /> {t('admin.estimator.line_items')}</h3>
                    <button
                        onClick={() => setShowCatalogPicker(p => !p)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                    >
                        <FaPlus size={10} /> {t('admin.estimator.add_item')}
                    </button>
                </div>

                {/* Inline catalog picker */}
                <AnimatePresence>
                    {showCatalogPicker && (
                        <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden border-b border-gray-100"
                        >
                            <div className="p-3 bg-gray-50">
                                <div className="flex flex-col gap-2 mb-3 lg:flex-row">
    <div className="relative flex-1">
      <FaSearch className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" size={12} />
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={t('admin.estimator.search_add')}
        autoFocus
        className="py-2 pr-3 pl-8 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      {search && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
          {filteredCatalog.length}
        </span>
      )}
    </div>
                                    <div className="flex overflow-x-auto flex-nowrap gap-1 lg:flex-wrap lg:overflow-visible">
                                        <button
                                            onClick={() => setCatFilter(null)}
                                            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${!catFilter ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                                        >{t('admin.estimator.filter_all')}</button>
                                        {catalogCats.map(catName => {
                                            const col = getCatColor(categories, catName)
                                            return (
                                                <button
                                                    key={catName}
                                                    onClick={() => setCatFilter(catFilter === catName ? null : catName)}
                                                    className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${catFilter === catName ? col.badge + ' shadow-sm' : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'}`}
                                                >{catName}</button>
                                            )
                                        })}
                                    </div>
                                </div>
                                <div className="overflow-y-auto space-y-1 max-h-52">
                                    {filteredCatalog.length === 0 && (
                                        <div className="py-4 text-xs italic text-center text-gray-400">
                                            {search || catFilter ? t('admin.estimator.no_filter_match') : t('admin.estimator.search_hint')}
                                        </div>
                                    )}
                                    {filteredCatalog.map(item => {
                                        const col = getCatColor(categories, item.cat)
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => addLine(item)}
                                                className="flex gap-2 items-center px-3 py-2 w-full text-sm text-left bg-white rounded-lg border border-gray-100 transition-colors hover:bg-primary/5"
                                            >
                                                <div className={`w-2 h-2 rounded-full shrink-0 ${col.dot}`} />
                                                <span className="flex-1 text-gray-800 truncate">{item.name}</span>
                                                <span className="text-xs text-gray-400 shrink-0">${fmt(item.price)}/{item.unit}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {estimate.lines.length === 0 ? (
                    <div className="py-10 text-sm text-center text-gray-400">
                        {t('admin.estimator.no_lines')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs tracking-wide text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
                                    <th className="px-3 py-2 w-8 text-center">#</th>
                                    <th className="px-3 py-2 text-left">{t('admin.estimator.col_description')}</th>
                                    <th className="px-3 py-2 text-center">{t('admin.estimator.col_unit')}</th>
                                    <th className="px-3 py-2 text-center">{t('admin.estimator.col_qty')}</th>
                                    <th className="px-3 py-2 text-center">{t('admin.estimator.col_unit_price')}</th>
                                    <th className="px-3 py-2 text-right">{t('admin.estimator.col_total')}</th>
                                    <th className="px-3 py-2 text-right">{t('admin.estimator.col_margin')}</th>
                                    <th className="w-8" />
                                </tr>
                            </thead>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} accessibility={{ container: document.body }}>
                                <SortableContext items={estimate.lines.map((_, i) => `line-${i}`)} strategy={verticalListSortingStrategy}>
                                    <tbody>
                                        {estimate.lines.map((line, idx) => (
                                            <SortableLineItemRow
                                                key={`${line.id}-${idx}`}
                                                id={`line-${idx}`}
                                                line={line}
                                                index={idx}
                                                onChange={updateLine}
                                                onRemove={removeLine}
                                                categories={categories}
                                            />
                                        ))}
                                    </tbody>
                                </SortableContext>
                            </DndContext>
                        </table>
                    </div>
                )}

                {/* Category breakdown strip */}
                {estimate.lines.length > 0 && (() => {
                    const bycat = {}
                    for (const l of estimate.lines) {
                        const c = l.cat || '—'
                        bycat[c] = (bycat[c] || 0) + (l.qty || 0) * (l.price || 0)
                    }
                    const entries = Object.entries(bycat).filter(([, v]) => v > 0)
                    if (entries.length < 2) return null
                    return (
                        <div className="flex flex-wrap gap-2 px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
                            <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold self-center shrink-0">{t('admin.estimator.by_category')}</span>
                            {entries.map(([cat, total]) => {
                                const col = getCatColor(categories, cat)
                                return (
                                    <span key={cat} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${col.badge}`}>
                                        {cat} · ${fmt(total)}
                                    </span>
                                )
                            })}
                        </div>
                    )
                })()}
            </div>

            {/* Summary sidebar cards */}
            {estimate.lines.length > 0 && (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {[
                            { label: t('admin.estimator.total_sale'), value: `$${fmt(totalVenta)}`, color: 'text-gray-800', bg: 'bg-white' },
                            { label: t('admin.estimator.total_cost'),  value: `$${fmt(totalCost)}`,  color: 'text-red-600',  bg: 'bg-red-50' },
                            { label: t('admin.estimator.profit'),      value: `$${fmt(utilidad)}`,   color: 'text-emerald-700', bg: 'bg-emerald-50' },
                            { label: t('admin.estimator.col_margin'),  value: pct(margin),            color: margin >= TARGET_MARGIN ? 'text-emerald-700' : 'text-amber-600', bg: margin >= TARGET_MARGIN ? 'bg-emerald-50' : 'bg-amber-50' },
                        ].map(({ label, value, color, bg }) => (
                            <div key={label} className={`p-3 text-center rounded-xl border border-gray-200 shadow-sm ${bg}`}>
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
                                <p className={`text-lg font-bold ${color}`}>{value}</p>
                            </div>
                        ))}
                    </div>
                    {/* Price Simulator — Simulador de precio */}
                    <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 px-4 py-3 rounded-xl border text-sm shadow-sm ${diferencia > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                        <div className="flex gap-2 items-center text-xs font-semibold tracking-wide text-gray-500 uppercase shrink-0"><FaCalculator size={11} /> {t('admin.estimator.simulator_label')} · {(TARGET_MARGIN * 100).toFixed(0)}% {t('admin.estimator.target_label')}</div>
                        <div className="flex flex-wrap gap-6">
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase tracking-wide block">{t('admin.estimator.min_price')}</span>
                                <span className="font-bold text-gray-700">${fmt(precioMin)}</span>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 uppercase tracking-wide block">{t('admin.estimator.diff_vs_actual')}</span>
                                <span className={`font-bold ${diferencia > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {diferencia > 0 ? '+' : ''}{fmt(diferencia) === '0.00' ? '—' : `$${fmt(Math.abs(diferencia))} ${diferencia > 0 ? t('admin.estimator.below_target') : t('admin.estimator.above_target')}`}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Schedule Editor */}
            <PaymentScheduleEditor
                schedule={estimate.paymentSchedule || []}
                totalVenta={totalVenta}
                onChange={sched => onChange('paymentSchedule', sched)}
                dbPresets={conSchedulePresets}
            />

            {/* Preview toggle */}
            <div className="flex justify-center">
                <button
                    onClick={() => setShowPreview(p => !p)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all shadow-sm ${
                        showPreview
                            ? 'bg-primary text-white border-primary hover:bg-primary/90'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                >
                    {showPreview ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                    {showPreview ? t('admin.estimator.hide_preview') : t('admin.estimator.show_preview')}
                </button>
            </div>

            {/* Inline preview */}
            <AnimatePresence>
                {showPreview && (
                    <motion.div
                        key="inline-preview"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm"
                    >
                        <EstimatePreview estimate={estimate} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// ── ESTIMATES LIST ────────────────────────────────────────────────────
function EstimatesList({ estimates, loading, error, reload, onResume, onPreview, session, conSchedulePresets = [] }) {
    const { t } = useTranslation()
    const { items: dbItems, loading: catalogLoading } = useCatalogItems()
    const [search,    setSearch]    = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [expanded,  setExpanded]  = useState({})
    const [toggling,  setToggling]  = useState(null)
    const [cloning,   setCloning]   = useState(null)

    const searchFiltered = useMemo(() => {
        const q = search.toLowerCase().trim()
        if (!q) return estimates
        return estimates.filter(e =>
            e.number?.toLowerCase().includes(q) ||
            e.client?.toLowerCase().includes(q) ||
            e.address?.toLowerCase().includes(q)
        )
    }, [estimates, search])

    const filtered = useMemo(() => {
        let result = searchFiltered
        if (statusFilter) {
            result = result.filter(e => (e.status || 'draft') === statusFilter)
        }
        return result
    }, [searchFiltered, statusFilter])

    const groups = useMemo(() => {
        const map = {}
        for (const est of filtered) {
            const key   = (est.client || '').trim().toLowerCase() || '__none__'
            const label = (est.client || '').trim() || null
            if (!map[key]) map[key] = { label, key, items: [] }
            map[key].items.push(est)
        }
        for (const g of Object.values(map)) {
            g.items.sort((a, b) => (a.date || '') < (b.date || '') ? -1 : 1)
        }
        return Object.values(map).sort((a, b) => {
            if (a.key === '__none__') return 1
            if (b.key === '__none__') return -1
            return a.key.localeCompare(b.key)
        })
    }, [filtered])

    const isExpanded = (key) => !!search || !!expanded[key]
    const toggleGroup = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))

    const handleResume = useCallback(async (est) => {
        const catalogMap = {}
        for (const item of (catalogLoading ? CATALOG : dbItems)) catalogMap[item.id] = item
        const lines = resolveLines(est.lines || [], catalogMap)
        const base = {
            id:              est.id,
            number:          est.number          || '',
            date:            est.date            || new Date().toISOString().split('T')[0],
            client:          est.client          || '',
            address:         est.address         || '',
            addressDetails:  est.address_details || { address: '', lat: null, lng: null, zipCode: '' },
            subtitle:        est.subtitle        || '',
            validDays:       est.valid_days      || 10,
            notes:           est.notes           || '',
            paymentSchedule: (Array.isArray(est.payment_schedule) && est.payment_schedule.length > 0) ? est.payment_schedule : getStandardPaymentSchedule(conSchedulePresets),
            lines,
            status:          est.status          || 'draft',
            updatedAt:       est.updated_at      || null,
        }
        onResume(await enrichEstimateContact(base, session))
    }, [catalogLoading, dbItems, onResume, conSchedulePresets, session])

    const handlePreview = useCallback((est) => {
        const catalogMap = {}
        for (const item of (catalogLoading ? CATALOG : dbItems)) catalogMap[item.id] = item
        const lines = resolveLines(est.lines || [], catalogMap)
        const base = {
            id:              est.id,
            number:          est.number          || '',
            date:            est.date            || new Date().toISOString().split('T')[0],
            client:          est.client          || '',
            address:         est.address         || '',
            addressDetails:  est.address_details || { address: '', lat: null, lng: null, zipCode: '' },
            subtitle:        est.subtitle        || '',
            validDays:       est.valid_days      || 10,
            notes:           est.notes           || '',
            paymentSchedule: (Array.isArray(est.payment_schedule) && est.payment_schedule.length > 0) ? est.payment_schedule : getStandardPaymentSchedule(conSchedulePresets),
            lines,
            status:          est.status          || 'draft',
            updatedAt:       est.updated_at      || null,
        }
        onPreview(base)
        enrichEstimateContact(base, session).then(enriched => {
            if (enriched !== base) onPreview(enriched)
        })
    }, [catalogLoading, dbItems, onPreview, conSchedulePresets, session])

    const handleArchive = async (id, e) => {
        e.stopPropagation()
        if (!window.confirm(t('admin.estimator.archive_confirm'))) return
        try {
            const user = session?.user
            await archiveEstimate(id, user?.id, user?.email, user?.user_metadata?.full_name || user?.email)
            reload()
        } catch (err) { alert(err.message) }
    }

    const handleToggleStatus = async (est, e) => {
        e.stopPropagation()
        const next = est.status === 'sent' ? 'draft' : 'sent'
        setToggling(est.id)
        try { await markEstimateStatus(est.id, next); reload() }
        catch (err) { alert(err.message) }
        finally { setToggling(null) }
    }

    const handleClone = async (est, e) => {
        e.stopPropagation()
        setCloning(est.id)
        try {
            await cloneEstimate(est.id, null)
            reload()
        } catch (err) {
            alert(err.message)
        } finally {
            setCloning(null)
        }
    }

    return (
        <div>
            <div className="flex gap-2 items-center mb-4">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={12} />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.estimator.estimates_search_placeholder')}
                        className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
                <button onClick={reload} className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:bg-gray-50" title={t('admin.estimator.refresh')}>
                    <FaHistory size={13} />
                </button>
            </div>

            {/* Status filter pills */}
            <div className="flex flex-wrap gap-1.5 mb-4">
                <button
                    onClick={() => setStatusFilter('')}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${!statusFilter ? 'text-white bg-primary' : 'text-gray-500 bg-white border border-gray-200 hover:bg-gray-50'}`}
                >{t('admin.estimator.filter_all')} ({searchFiltered.length})</button>
                {['draft', 'sent'].map(s => {
                    const count = searchFiltered.filter(e => (e.status || 'draft') === s).length
                    const activeColor = s === 'draft' ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'
                    return (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                        className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${statusFilter === s ? activeColor : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                    >{t(`admin.estimator.filter_${s}`)} ({count})</button>
                    )
                })}
            </div>

            {loading && (
                <div className="space-y-2">
                    {[...Array(3)].map((_,i) => (
                        <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                    ))}
                </div>
            )}

            {!loading && error && (
                <p className="py-10 text-sm text-center text-red-500">{error}</p>
            )}

            {!loading && !error && groups.length === 0 && (
                <p className="py-14 text-sm text-center text-gray-400">
                    {search
                        ? t('admin.estimator.estimates_no_results')
                        : t('admin.estimator.estimates_empty')}
                </p>
            )}

            {!loading && !error && groups.length > 0 && (
                <div className="grid gap-2">
                    {groups.map(group => {
                        const isOpen = isExpanded(group.key)
                        const latestEst = group.items.reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                        const latestSent = group.items.filter(e => (e.status || 'draft') === 'sent')
                            .reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                        const shownEst = latestSent || latestEst
                        const latestDate = group.items.reduce((d, e) => (!d || e.updated_at > d ? e.updated_at : d), null)
                        return (
                            <div key={group.key} className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                                {/* Group header */}
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.key)}
                                    className="flex gap-3 items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                                >
                                    <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                        <FaBuilding className="text-primary" size={13} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">
                                            {group.label || <span className="italic text-gray-400">{t('admin.estimator.no_client')}</span>}
                                        </p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">
                                            {t('admin.estimator.estimates_count', { count: group.items.length })}
                                            {latestDate && <> · {new Date(latestDate).toLocaleDateString()}</>}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-bold text-primary">${fmt(shownEst?.total || 0)}</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">{t('admin.estimator.latest_total')} <strong className={latestSent ? 'text-emerald-600' : 'text-rose-500'}>{latestSent ? t('admin.estimator.latest_emph_sent') : t('admin.estimator.latest_emph_not_sent')}</strong></p>
                                    </div>
                                    <div className="text-gray-400 shrink-0">
                                        {isOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                                    </div>
                                </button>

                                {/* Expanded estimate sub-rows */}
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            key="rows"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.18 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-4 pb-3 space-y-2 border-t border-gray-100">
                                                {group.items.map((est, idx) => {
                                                    const date = est.date
                                                        ? new Date(est.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                                        : ''
                                                    const lineCount = (est.lines || []).length
                                                    const isSent = est.status === 'sent'
                                                    return (
                                                        <div key={est.id} className="flex gap-3 items-center pt-2.5">
                                                            {/* Left column — info */}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-wrap gap-1.5 items-center">
                                                                    <span className="text-[10px] font-bold text-gray-500">{idx < 3 ? t(`admin.estimator.ordinal_${idx + 1}`) : t('admin.estimator.ordinal_other', { n: idx + 1 })}</span>
                                                                    <span className="font-mono text-[11px] text-gray-400">{est.number}</span>
                                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                                                        isSent
                                                                            ? 'bg-emerald-100 text-emerald-700'
                                                                            : 'bg-gray-100 text-gray-500'
                                                                    }`}>{est.status}</span>
                                                                </div>
                                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                                    {date}{est.address ? ` · ${est.address}` : ''}
                                                                    {lineCount > 0 && ` · ${t('admin.estimator.items_count', { count: lineCount })}`}
                                                                </p>
                                                                <p className="text-sm font-bold text-primary mt-0.5">${fmt(est.total || 0)}</p>
                                                            </div>

                                                            {/* Right column — vertical button stack */}
                                                            <div className="flex flex-col gap-1 shrink-0">
                                                                <button
                                                                    onClick={() => handleResume(est)}
                                                                    disabled={catalogLoading}
                                                                    className="px-2.5 py-1 text-[11px] font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-wait"
                                                                >
                                                                    {catalogLoading ? t('admin.estimator.loading') : t('admin.estimator.draft_resume')}
                                                                </button>
                                                                <button
                                                                    onClick={() => handlePreview(est)}
                                                                    className="px-2.5 py-1 text-[11px] font-semibold text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-all flex items-center gap-1 justify-center"
                                                                >
                                                                    <FaEye size={9} /> {t('admin.estimator.preview')}
                                                                </button>
                                                                <button
                                                                    onClick={e => handleToggleStatus(est, e)}
                                                                    disabled={toggling === est.id}
                                                                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-all ${
                                                                        toggling === est.id ? 'opacity-50 cursor-wait' : ''
                                                                    } text-gray-600 bg-gray-100 hover:bg-gray-200`}
                                                                >
                                                                    {isSent ? t('admin.estimator.mark_draft') : t('admin.estimator.mark_sent')}
                                                                </button>
                                                                <button
                                                                    onClick={e => handleArchive(est.id, e)}
                                                                    disabled={toggling === est.id}
                                                                    className="px-2.5 py-1 text-[11px] font-semibold text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-all"
                                                                >
                                                                    {t('admin.estimator.archive')}
                                                                </button>
                                                                <button
                                                                    onClick={e => handleClone(est, e)}
                                                                    disabled={cloning === est.id}
                                                                    className="px-2.5 py-1 text-[11px] font-semibold text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-all disabled:opacity-50 flex items-center gap-1 justify-center"
                                                                >
                                                                    {cloning === est.id ? <FaSpinner size={9} className="animate-spin" /> : <FaCopy size={9} />}
                                                                    {t('admin.estimator.clone')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// ── LOGS SUB-TAB (Archived Estimates) ─────────────────────────────────────────
function LogsSubTab() {
    const { t } = useTranslation()
    const { estimates, loading, error, reload } = useArchivedEstimates()
    const [searchQuery, setSearchQuery] = useState('')
    const [clientFilter, setClientFilter] = useState('')
    const [archiverFilter, setArchiverFilter] = useState('')

    const uniqueClients = useMemo(() => {
        const set = new Set(estimates.map(e => (e.client || '').trim()).filter(Boolean))
        return Array.from(set).sort((a, b) => a.localeCompare(b))
    }, [estimates])

    const uniqueArchivers = useMemo(() => {
        const set = new Set(estimates.map(e => e.archived_by_name || e.archived_by_email || '—').filter(n => n !== '—'))
        return Array.from(set).sort((a, b) => a.localeCompare(b))
    }, [estimates])

    const groups = useMemo(() => {
        let filtered = estimates
        const q = searchQuery.trim().toLowerCase()
        if (q) {
            filtered = filtered.filter(est =>
                (est.number || '').toLowerCase().includes(q) ||
                (est.client || '').toLowerCase().includes(q) ||
                (est.address || '').toLowerCase().includes(q) ||
                (est.archived_by_name || '').toLowerCase().includes(q) ||
                (est.archived_by_email || '').toLowerCase().includes(q)
            )
        }
        if (clientFilter) {
            filtered = filtered.filter(est => (est.client || '').trim() === clientFilter)
        }
        if (archiverFilter) {
            filtered = filtered.filter(est => (est.archived_by_name || est.archived_by_email || '—') === archiverFilter)
        }
        const map = {}
        for (const est of filtered) {
            const key = (est.client || '').trim().toLowerCase() || '__none__'
            const label = (est.client || '').trim() || null
            if (!map[key]) map[key] = { label, key, items: [] }
            map[key].items.push(est)
        }
        return Object.values(map).sort((a, b) => {
            if (a.key === '__none__') return 1
            if (b.key === '__none__') return -1
            return a.key.localeCompare(b.key)
        })
    }, [estimates, searchQuery, clientFilter, archiverFilter])

    if (loading) return <p className="py-10 text-center text-gray-400">{t('admin.estimator.loading')}</p>
    if (error)   return <p className="py-10 text-center text-red-400">{error}</p>
    if (estimates.length === 0) return <p className="py-14 text-sm text-center text-gray-400">{t('admin.estimator.logs_empty')}</p>

    return (
        <div className="space-y-4">
            <div className="flex gap-3 items-center">
                <h2 className="text-lg font-bold text-gray-800">{t('admin.estimator.logs_title')}</h2>
                <button onClick={reload} className="p-2 text-gray-400 rounded-xl border border-gray-200 transition-all hover:bg-gray-50">
                    <FaSync size={13} />
                </button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('admin.estimator.logs_search_placeholder')}
                    className="flex-1 min-w-[12rem] px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <select
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                    className="min-w-[10rem] px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                    <option value="">{t('admin.estimator.logs_filter_all_clients')}</option>
                    {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                    value={archiverFilter}
                    onChange={e => setArchiverFilter(e.target.value)}
                    className="min-w-[10rem] px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                    <option value="">{t('admin.estimator.logs_filter_all_users')}</option>
                    {uniqueArchivers.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
            </div>
            <div className="grid gap-2">
                {groups.map(group => {
                    const latestEst = group.items.reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                    return (
                        <div key={group.key} className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex gap-3 items-center px-4 py-3">
                                <div className="p-2 bg-gray-100 rounded-xl shrink-0">
                                    <FaHistory className="text-gray-500" size={13} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">
                                        {group.label || <span className="italic text-gray-400">{t('admin.estimator.no_client')}</span>}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        {t('admin.estimator.estimates_count', { count: group.items.length })}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-primary">${fmt(latestEst?.total || 0)}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{t('admin.estimator.latest_total')}</p>
                                </div>
                            </div>
                            <div className="px-4 pb-3 space-y-2 border-t border-gray-100">
                                {group.items.map(est => {
                                    const date = est.updated_at
                                        ? new Date(est.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                        : ''
                                    const archiverName = est.archived_by_name || est.archived_by_email || '—'
                                    return (
                                        <div key={est.id} className="flex gap-3 items-center pt-2.5">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap gap-1.5 items-center">
                                                    <span className="font-mono text-[11px] text-gray-400">{est.number}</span>
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-600">{t('admin.estimator.archived_status')}</span>
                                                </div>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {date}{est.address ? ` · ${est.address}` : ''}
                                                </p>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {t('admin.estimator.archived_by_label')}: <span className="font-semibold text-gray-600">{archiverName}</span>
                                                </p>
                                                <p className="text-sm font-bold text-primary mt-0.5">${fmt(est.total || 0)}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── CON SCHEDULE PRESETS TAB ──────────────────────────────────────────────────
function ConSchedulePresetsTab({
    schedulePresets, schedulePresetsLoading,
    createSchedulePreset, updateSchedulePreset, deleteSchedulePreset,
    setDefaultPreset, clearDefaultPreset, reloadSchedulePresets,
}) {
    const { t } = useTranslation()

    const [showAddPreset, setShowAddPreset] = useState(false)
    const [newPresetName, setNewPresetName] = useState('')
    const [newPresetRows, setNewPresetRows] = useState([{ label: '', pct: 0 }])
    const [addingPreset, setAddingPreset] = useState(false)
    const [editingPresetId, setEditingPresetId] = useState(null)
    const [editPresetDraft, setEditPresetDraft] = useState({ name: '', rows: [{ label: '', pct: 0 }] })

    return (
        <div className="space-y-4">
            <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-amber-400">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="flex gap-2 items-center font-bold text-gray-800">
                            <FaMoneyBillWave size={13} className="text-amber-500" />
                            {t('admin.estimator.schedule_presets_title')}
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">{t('admin.estimator.schedule_presets_subtitle')}</p>
                    </div>
                    <button
                        onClick={() => { setShowAddPreset(true); setEditingPresetId(null); setNewPresetName(''); setNewPresetRows([{ label: '', pct: 0 }]) }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-all"
                    >
                        <FaPlus size={10} /> {t('admin.estimator.schedule_preset_add')}
                    </button>
                </div>

                {schedulePresetsLoading && <p className="text-xs text-gray-400">{t('admin.estimator.loading')}</p>}

                {/* Preset list */}
                <div className="space-y-2">
                    {(schedulePresets || []).map(preset => {
                        const rows = preset.rows || []
                        const total = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                        const isEditing = editingPresetId === preset.id
                        const isDefault = !!preset.is_default
                        return (
                            <div key={preset.id} className={`overflow-hidden rounded-xl border ${isDefault ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
                                {!isEditing ? (
                                    <div className="flex justify-between items-center px-4 py-3 bg-gray-50/60">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex gap-2 items-center">
                                                <span className="text-sm font-bold text-gray-800 truncate">{preset.name}</span>
                                                {isDefault && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                                                        <FaStar size={7} /> {t('admin.estimator.schedule_preset_is_default')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-gray-400">
                                                {rows.length} {t('admin.estimator.schedule_preset_rows')} · {total}%
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 items-center shrink-0">
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        if (isDefault) await clearDefaultPreset(preset.id)
                                                        else await setDefaultPreset(preset.id)
                                                    } catch (e) { alert(e.message) }
                                                }}
                                                className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-lg border transition-all ${
                                                    isDefault
                                                        ? 'text-amber-700 bg-amber-100 border-amber-200 hover:bg-amber-200'
                                                        : 'text-gray-400 bg-white border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200'
                                                }`}
                                                title={isDefault ? t('admin.estimator.schedule_preset_is_default') : t('admin.estimator.schedule_preset_set_default')}
                                            >
                                                {isDefault ? <FaStar size={9} /> : <FaRegStar size={9} />}
                                                {isDefault ? t('admin.estimator.schedule_preset_is_default') : t('admin.estimator.schedule_preset_set_default')}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setEditingPresetId(preset.id)
                                                    setEditPresetDraft({ name: preset.name, rows: rows.map(r => ({ label: r.label || '', pct: r.pct || 0 })) })
                                                    setShowAddPreset(false)
                                                }}
                                                className="px-2 py-1 text-[10px] font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-all"
                                            >
                                                {t('admin.estimator.schedule_preset_edit')}
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(t('admin.estimator.schedule_preset_delete_confirm'))) return
                                                    try { await deleteSchedulePreset(preset.id); reloadSchedulePresets() } catch (e) { alert(e.message) }
                                                }}
                                                className="px-2 py-1 text-[10px] font-bold text-red-500 bg-white border border-gray-200 rounded-lg hover:bg-red-50 transition-all"
                                            >
                                                {t('admin.estimator.schedule_preset_delete')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-4 space-y-3">
                                        <input
                                            value={editPresetDraft.name}
                                            onChange={e => setEditPresetDraft(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder={t('admin.estimator.schedule_preset_name')}
                                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                        />
                                        <div className="space-y-1.5">
                                            {editPresetDraft.rows.map((r, idx) => (
                                                <div key={idx} className="flex gap-2 items-center">
                                                    <input
                                                        value={r.label}
                                                        onChange={e => {
                                                            const next = [...editPresetDraft.rows]
                                                            next[idx] = { ...next[idx], label: e.target.value }
                                                            setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                        }}
                                                        placeholder={t('admin.estimator.milestone_label')}
                                                        className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30 min-w-0"
                                                    />
                                                    <div className="relative w-16 shrink-0">
                                                        <input
                                                            type="number"
                                                            value={r.pct}
                                                            min={0} max={100} step={1}
                                                            onChange={e => {
                                                                const next = [...editPresetDraft.rows]
                                                                next[idx] = { ...next[idx], pct: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                                                                setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                            }}
                                                            className="w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                                        />
                                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            const next = editPresetDraft.rows.filter((_, i) => i !== idx)
                                                            setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                        }}
                                                        disabled={editPresetDraft.rows.length <= 1}
                                                        className="flex justify-center p-1 w-7 text-gray-300 rounded-lg transition-all hover:text-red-500 hover:bg-red-50 disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                                    >
                                                        <FaTimesCircle size={11} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <button
                                                onClick={() => setEditPresetDraft(prev => ({ ...prev, rows: [...prev.rows, { label: '', pct: 0 }] }))}
                                                className="flex gap-1 items-center text-xs font-semibold text-amber-600 transition-all hover:text-amber-700"
                                            >
                                                <FaPlus size={9} /> {t('admin.estimator.schedule_preset_new_row')}
                                            </button>
                                            <span className={`text-xs font-bold ml-auto ${Math.abs(editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0) - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                {t('admin.estimator.schedule_preset_total_pct')}: {editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)}%
                                            </span>
                                        </div>
                                        <div className="flex gap-2 pt-1">
                                            <button
                                                onClick={async () => {
                                                    const total2 = editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                                                    if (Math.abs(total2 - 100) >= 0.01) { alert(t('admin.estimator.schedule_preset_must_equal_100')); return }
                                                    try {
                                                        await updateSchedulePreset(preset.id, { name: editPresetDraft.name, rows: editPresetDraft.rows })
                                                        setEditingPresetId(null)
                                                        reloadSchedulePresets()
                                                    } catch (e) { alert(e.message) }
                                                }}
                                                className="flex-1 py-2 text-xs font-bold text-white bg-amber-500 rounded-xl transition-all hover:bg-amber-600"
                                            >
                                                {t('admin.estimator.save')}
                                            </button>
                                            <button
                                                onClick={() => setEditingPresetId(null)}
                                                className="flex-1 py-2 text-xs font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200"
                                            >
                                                {t('admin.estimator.cancel')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Add form */}
                {showAddPreset && (
                    <div className="p-4 mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50/40">
                        <input
                            value={newPresetName}
                            onChange={e => setNewPresetName(e.target.value)}
                            placeholder={t('admin.estimator.schedule_preset_name')}
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                        />
                        <div className="space-y-1.5">
                            {newPresetRows.map((r, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        value={r.label}
                                        onChange={e => {
                                            const next = [...newPresetRows]
                                            next[idx] = { ...next[idx], label: e.target.value }
                                            setNewPresetRows(next)
                                        }}
                                        placeholder={t('admin.estimator.milestone_label')}
                                        className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30 min-w-0"
                                    />
                                    <div className="relative w-16 shrink-0">
                                        <input
                                            type="number"
                                            value={r.pct}
                                            min={0} max={100} step={1}
                                            onChange={e => {
                                                const next = [...newPresetRows]
                                                next[idx] = { ...next[idx], pct: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                                                setNewPresetRows(next)
                                            }}
                                            className="w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                    </div>
                                    <button
                                        onClick={() => setNewPresetRows(prev => prev.filter((_, i) => i !== idx))}
                                        disabled={newPresetRows.length <= 1}
                                        className="flex justify-center p-1 w-7 text-gray-300 rounded-lg transition-all hover:text-red-500 hover:bg-red-50 disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                    >
                                        <FaTimesCircle size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 items-center">
                            <button
                                onClick={() => setNewPresetRows(prev => [...prev, { label: '', pct: 0 }])}
                                className="flex gap-1 items-center text-xs font-semibold text-amber-600 transition-all hover:text-amber-700"
                            >
                                <FaPlus size={9} /> {t('admin.estimator.schedule_preset_new_row')}
                            </button>
                            <span className={`text-xs font-bold ml-auto ${Math.abs(newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0) - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {t('admin.estimator.schedule_preset_total_pct')}: {newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0)}%
                            </span>
                        </div>
                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={async () => {
                                    const total2 = newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                                    if (!newPresetName.trim()) { alert(t('admin.estimator.schedule_preset_name')); return }
                                    if (Math.abs(total2 - 100) >= 0.01) { alert(t('admin.estimator.schedule_preset_must_equal_100')); return }
                                    setAddingPreset(true)
                                    try {
                                        await createSchedulePreset({ name: newPresetName.trim(), rows: newPresetRows })
                                        setShowAddPreset(false)
                                        setNewPresetName('')
                                        setNewPresetRows([{ label: '', pct: 0 }])
                                        reloadSchedulePresets()
                                    } catch (e) { alert(e.message) }
                                    finally { setAddingPreset(false) }
                                }}
                                disabled={addingPreset}
                                className="flex-1 py-2 text-xs font-bold text-white bg-amber-500 rounded-xl transition-all hover:bg-amber-600 disabled:opacity-60"
                            >
                                {addingPreset ? t('admin.estimator.saving') : t('admin.estimator.save')}
                            </button>
                            <button
                                onClick={() => { setShowAddPreset(false); setNewPresetName(''); setNewPresetRows([{ label: '', pct: 0 }]) }}
                                className="flex-1 py-2 text-xs font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200"
                            >
                                {t('admin.estimator.cancel')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

// ── EDITOR TAB WRAPPER (Catalog Editor | Categories & Sub-services | Price Groups | Logs) ──
function EditorTabWrapper({ canEdit, session, conSchedulePresets, conSchedulePresetsLoading, conCreatePreset, conUpdatePreset, conDeletePreset, conSetDefault, conClearDefault, conReloadPresets }) {
    const { t } = useTranslation()
    const userId = session?.user?.id || null
    const [subTab, setSubTab] = usePersistentState(`est_con_editor_tab_${userId}`, 'catalog')
    return (
        <div>
            <div className="flex mb-5 border-b border-gray-200 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                {[
                    { id: 'catalog',      label: 'sub_tab_catalog_editor', icon: FaEdit },
                    { id: 'cat_sub',      label: 'sub_tab_cat_sub',        icon: FaTags },
                    { id: 'price_groups', label: 'sub_tab_price_groups',   icon: FaMoneyBillWave },
                    { id: 'schedules',    label: 'sub_tab_schedules',      icon: FaCalendarAlt },
                    { id: 'logs',         label: 'sub_tab_logs',           icon: FaHistory },
                ].map(({ id, label, icon: Icon }) => (
                    <button
                        key={id}
                        onClick={() => setSubTab(id)}
                        className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap shrink-0 ${
                            subTab === id
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Icon size={13} /> {t(`admin.estimator.${label}`)}
                    </button>
                ))}
            </div>
            {subTab === 'catalog'      && <CatalogEditorTab canEdit={canEdit} session={session} />}
            {subTab === 'cat_sub'      && <CategoriesSubServicesTab canEdit={canEdit} session={session} />}
            {subTab === 'price_groups' && <PriceGroupsManager canEdit={canEdit} />}
            {subTab === 'schedules'    && (
                <ConSchedulePresetsTab
                    schedulePresets={conSchedulePresets}
                    schedulePresetsLoading={conSchedulePresetsLoading}
                    createSchedulePreset={conCreatePreset}
                    updateSchedulePreset={conUpdatePreset}
                    deleteSchedulePreset={conDeletePreset}
                    setDefaultPreset={conSetDefault}
                    clearDefaultPreset={conClearDefault}
                    reloadSchedulePresets={conReloadPresets}
                />
            )}
            {subTab === 'logs'         && <LogsSubTab />}
        </div>
    )
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const TABS = [
    { id: 'estimados', label: 'Estimados',  icon: FaFileInvoiceDollar },
    { id: 'builder',   label: 'Constructor', icon: FaCalculator },
    { id: 'editor',    label: 'Editor',      icon: FaEdit },
]

const DRAFT_TYPE = 'estimate_builder'

export default function EstimateBuilder({ session, onReturn, onLogout }) {
    const { t } = useTranslation()
    const userId = session?.user?.id || null
    const { role, isSuperAdmin, isPM, permissions, loading: roleLoading } = useRole()
    const canEditCatalog = !!permissions['admin.estimator.edit_catalog']
    const hasCon = !!permissions['admin.estimator.construction']
    const hasArch = !!permissions['admin.estimator.architecture']
    const showModeToggle = hasCon && hasArch
    const [mode, setMode] = usePersistentState(`est_mode_${userId}`, 'construction')

    // Lock mode for permission-specific estimators
    useEffect(() => {
        if (roleLoading) return
        if (!hasCon && hasArch) setMode('arch')
        else if (hasCon && !hasArch) setMode('construction')
    }, [roleLoading, hasCon, hasArch]) // eslint-disable-line react-hooks/exhaustive-deps
    const [tab,  setTab]  = usePersistentState(`est_con_tab_${userId}`, 'estimados')
    const [estimate, setEstimate] = useState(EMPTY_ESTIMATE)
    const [previewEst, setPreviewEst] = useState(null)
    const { estimates: estList, loading: estLoading, error: estError, reload: reloadEstimates, silentReload: silentReloadEstimates } = useEstimates()
    const {
        rows: conSchedulePresets, loading: conSchedulePresetsLoading,
        createSchedulePreset: conCreatePreset, updateSchedulePreset: conUpdatePreset, deleteSchedulePreset: conDeletePreset,
        setDefaultPreset: conSetDefault, clearDefaultPreset: conClearDefault, reload: conReloadPresets,
    } = useConSchedulePresets()

    // Auto-populate paymentSchedule from default preset when presets load and estimate is new/empty
    useEffect(() => {
        if (conSchedulePresets.length === 0) return
        const defaultPreset = conSchedulePresets.find(p => p.is_default)
        if (!defaultPreset) return
        setEstimate(prev => {
            if (prev.id) return prev
            if (prev.paymentSchedule && prev.paymentSchedule.length > 0) return prev
            return { ...prev, paymentSchedule: defaultPreset.rows.map(r => ({ label: r.label || '', pct: r.pct })) }
        })
    }, [conSchedulePresets])

    // ── Draft / auto-save state ──
    const draftTimerRef  = useRef(null)
    const draftDataRef   = useRef(null)   // holds loaded draft data until user acts
    const isFirstRender  = useRef(true)   // skip auto-save on initial mount
    const [isSaving,     setIsSaving]     = useState(false)
    const [savedAt,      setSavedAt]      = useState(null)   // last IndexedDB auto-save time
    const [draftPrompt,  setDraftPrompt]  = useState(null)   // { savedAt } or null
    const [isDbSaving,   setIsDbSaving]   = useState(false)  // Supabase save in progress
    const [dbSavedAt,    setDbSavedAt]    = useState(null)   // last Supabase save time
    const [isPushing,    setIsPushing]    = useState(false)  // Push to Sources in progress
    const markEstRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible (guard while saving)
    const { markRefreshed: markEstRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (!isDbSaving) {
                Promise.resolve(silentReloadEstimates()).finally(() => markEstRefreshedRef.current?.())
            }
        }, [isDbSaving, silentReloadEstimates]),
    })
    markEstRefreshedRef.current = markEstRefreshed

    // On mount: check for an existing draft
    useEffect(() => {
        const wasActive = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('construction_estimator_active') === 'true'
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('construction_estimator_active', 'true')

        loadDraft(DRAFT_TYPE, userId)
            .then(draft => {
                if (draft && draft.formData) {
                    if (wasActive) {
                        // Same tab minimize/restore or admin tab switch: silent restore
                        setEstimate(draft.formData)
                        draftDataRef.current = null
                    } else {
                        draftDataRef.current = draft.formData
                        setDraftPrompt({ savedAt: draft.savedAt })
                    }
                }
            })
            .catch(() => {})
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save on every estimate change (debounced 1.5s)
    // Skip: the very first render (initial state) and truly empty estimates
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        const isEmpty = estimate.lines.length === 0 && !estimate.client && !estimate.address && !estimate.subtitle
        if (isEmpty) return

        setIsSaving(true)
        clearTimeout(draftTimerRef.current)
        draftTimerRef.current = setTimeout(async () => {
            try {
                await saveDraft(DRAFT_TYPE, estimate, [], userId)
                setSavedAt(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
            } catch (e) {
                console.error('[EstimateBuilder] auto-save failed:', e)
            } finally {
                setIsSaving(false)
            }
        }, 1500)
        return () => clearTimeout(draftTimerRef.current)
    }, [estimate]) // eslint-disable-line react-hooks/exhaustive-deps

    // Flush pending draft to IndexedDB before tab close / hide
    useEffect(() => {
        let flushing = false
        const flush = () => {
            if (flushing || !draftTimerRef.current) return
            flushing = true
            clearTimeout(draftTimerRef.current)
            draftTimerRef.current = null
            saveDraft(DRAFT_TYPE, estimate, [], userId).catch(() => {})
        }
        window.addEventListener('beforeunload', flush)
        const onVis = () => { if (document.visibilityState === 'hidden') flush() }
        document.addEventListener('visibilitychange', onVis)
        return () => {
            window.removeEventListener('beforeunload', flush)
            document.removeEventListener('visibilitychange', onVis)
        }
    }, [estimate, userId])

    // ── Draft actions ──
    const handleResumeDraft = () => {
        if (draftDataRef.current) {
            setEstimate(draftDataRef.current)
        }
        setDraftPrompt(null)
        draftDataRef.current = null
    }

    const handleDiscardDraft = () => {
        deleteDraft(DRAFT_TYPE, userId).catch(() => {})
        setDraftPrompt(null)
        draftDataRef.current = null
    }

    const updateEstimate = useCallback((field, value) => {
        setEstimate(prev => ({ ...prev, [field]: value }))
    }, [])

    const handlePushToSources = useCallback(async () => {
        const ad = estimate.addressDetails || {}
        if (!ad.mondayItemId || !ad.mondayBoardId) return
        setIsPushing(true)
        const results = { monday: false, ghl: false, ghlDetail: '' }
        try {
            // ── Step 1a: Update item name ("Client | Address") ──
            await updateItemName(ad.mondayBoardId, ad.mondayItemId, `${estimate.client} | ${estimate.address}`, session)

            // ── Step 1b: Update client name column (simple string) ──
            await updateSimpleColumnValue(ad.mondayBoardId, ad.mondayItemId, MONDAY_CLIENT_NAME_COL, estimate.client, session)

            // ── Step 1c: Update location column (JSON) ──
            if (ad.lat && ad.lng) {
                await updateColumnValue(ad.mondayBoardId, ad.mondayItemId, MONDAY_ADDRESS_COL, { lat: String(ad.lat), lng: String(ad.lng), address: estimate.address }, session)
            }
            results.monday = true
            // Update baselines so badge disappears
            setEstimate(prev => ({
                ...prev,
                addressDetails: {
                    ...prev.addressDetails,
                    originalClient: prev.client,
                    originalAddress: prev.address,
                },
            }))
        } catch (err) {
            console.error('[Push to Sources] Monday error:', err)
        }
        // ── Step 2: Push to GHL ──
        try {
            const contact = await searchGHLContact('construction', {
                email: ad.clientEmail,
                phone: ad.clientPhone,
            })
            console.log('[Push Debug] GHL contact:', contact?.id, 'email:', ad.clientEmail, 'phone:', ad.clientPhone)
            if (!contact) {
                results.ghlDetail = 'Contact not found'
            } else {
                const [firstName, ...rest] = (estimate.client || '').trim().split(/\s+/)
                await updateGHLContact('construction', contact.id, {
                    firstName: firstName || '',
                    lastName: rest.join(' '),
                })
                const opps = await getContactOpportunities('construction', contact.id)
                const origAddr = (ad.originalAddress || '').toLowerCase()
                console.log('[Push Debug] opps count:', opps.length, 'origAddr:', origAddr)
                opps.forEach(o => console.log('[Push Debug] opp:', o.id, 'pipeline:', o.pipelineId, 'status:', o.status, 'name:', o.name))
                // Extract street number from original address for flexible matching
                const streetNum = origAddr.trim().split(/\s+/)[0] || ''
                const matching = opps.filter(o => {
                    if ((o.pipelineId !== VENTAS_PIPELINE_ID && o.pipelineId !== CONSTRUCCION_PIPELINE_ID) || o.status !== 'open') return false
                    const oppName = (o.name || '').toLowerCase()
                    // Try full substring match first, then fall back to street number match
                    return oppName.includes(origAddr) || (streetNum && oppName.includes(streetNum))
                }).sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : -1))
                console.log('[Push Debug] matching:', matching.length)
                if (matching[0]) {
                    // Parse existing opp name to preserve [Lxx] tag and middle sections
                    const parts = (matching[0].name || '').split(' - ')
                    const tagMatch = (parts[0] || '').match(/\[L\d+\]/)
                    const tag = tagMatch ? tagMatch[0] : ''
                    let newTitle
                    if (parts.length >= 3) {
                        const middle = parts.slice(1, -1).join(' - ')
                        newTitle = `${estimate.client} ${tag} - ${middle} - ${estimate.address}`.replace(/\s+/g, ' ').trim()
                    } else if (parts.length === 2) {
                        newTitle = `${estimate.client} ${tag} - ${estimate.address}`.replace(/\s+/g, ' ').trim()
                    } else {
                        newTitle = `${estimate.client} - ${estimate.address}`
                    }
                    await updateGHLOpportunity('construction', {
                        opportunityId: matching[0].id,
                        title: newTitle,
                        customFields: [
                            { id: GHL_CONFIG.construction.customFieldIds.Project_Address, field_value: estimate.address },
                            { id: GHL_CONFIG.construction.customFieldIds.Project_Name, field_value: estimate.client },
                        ],
                    })
                    results.ghl = true
                } else {
                    results.ghlDetail = 'Opportunity not found'
                }
            }
        } catch (err) {
            console.error('[Push to Sources] GHL error:', err)
            results.ghlDetail = err.message || 'GHL error'
        }
        setIsPushing(false)
        // ── Step 3: Toast ──
        if (results.monday && results.ghl) {
            alert(t('admin.estimator.push_success'))
        } else if (results.monday && !results.ghl) {
            alert(t('admin.estimator.push_partial', { detail: results.ghlDetail }))
        } else {
            alert(t('admin.estimator.push_error'))
        }
    }, [estimate, session, t])

    const handleAddToEstimate = useCallback((item) => {
        setEstimate(prev => ({
            ...prev,
            lines: [...prev.lines, { ...item, qty: 1, price: item.price }]
        }))
        setTab('builder')
    }, [])

    const handleSaveToDb = useCallback(async () => {
        if (isDbSaving) return
        if (estimate.lines.length === 0 && !estimate.client) {
            alert(t('admin.estimator.save_guard'))
            return
        }
        setIsDbSaving(true)
        try {
            const saved = await saveEstimate(estimate, userId)
            setEstimate(prev => ({
                ...prev,
                id: saved.id,
                paymentSchedule: (Array.isArray(saved.payment_schedule) && saved.payment_schedule.length > 0)
                    ? saved.payment_schedule
                    : prev.paymentSchedule,
            }))
            setDbSavedAt(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
            deleteDraft(DRAFT_TYPE, userId).catch(() => {})
        } catch (e) {
            console.error('[EstimateBuilder] db-save failed:', e)
            alert(t('admin.estimator.save_failed', { msg: e.message }))
        } finally {
            setIsDbSaving(false)
        }
    }, [estimate, isDbSaving, userId])

    const handleResumeFromList = useCallback((resolvedEst) => {
        setEstimate(resolvedEst)
        setDbSavedAt(
            resolvedEst.updatedAt
                ? new Date(resolvedEst.updatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : null
        )
        setSavedAt(null)
        deleteDraft(DRAFT_TYPE, userId).catch(() => {})
        setTab('builder')
    }, [userId])

    const handleReset = () => {
        if (window.confirm(t('admin.estimator.reset_confirm'))) {
            deleteDraft(DRAFT_TYPE, userId).catch(() => {})
            const base = EMPTY_ESTIMATE()
            base.paymentSchedule = getStandardPaymentSchedule(conSchedulePresets)
            setEstimate(base)
            setSavedAt(null)
            setDbSavedAt(null)
            setIsSaving(false)
            setTab('builder')
        }
    }

    return (
        <>
        <div className={mode === 'arch' ? 'hidden' : undefined}>
        <div className="min-h-screen bg-cloud">

            {/* ── Draft recovery modal ── */}
            <AnimatePresence>
                {draftPrompt && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40"
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 16 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 16 }}
                            className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl"
                        >
                            <div className="flex gap-3 items-center mb-3">
                                <div className="p-2.5 rounded-xl bg-violet-100">
                                    <FaHistory className="text-violet-600" size={18} />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-gray-800">{t('admin.estimator.draft_title')}</p>
                                    <p className="text-xs text-gray-400">
                                        {new Date(draftPrompt.savedAt).toLocaleString(undefined, {
                                            month: 'short', day: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            </div>
                            <p className="mb-5 text-sm text-gray-500">
                                {t('admin.estimator.draft_body')}
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleResumeDraft}
                                    className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 transition-all"
                                >
                                    {t('admin.estimator.draft_resume')}
                                </button>
                                <button
                                    onClick={handleDiscardDraft}
                                    className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-all"
                                >
                                    {t('admin.estimator.draft_fresh')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <button
                        onClick={onReturn}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                        title={t('admin.estimator.back')}
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="flex flex-1 gap-2 items-center min-w-0 text-xl font-bold text-primary"><FaFileInvoiceDollar size={18} className="shrink-0" /><span className="truncate">{t('admin.estimator.title')}</span></h1>
                    {showModeToggle && (
                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 gap-0.5 shadow-inner shrink-0">
                        <button
                            onClick={() => setMode('construction')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                mode === 'construction' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title={t('admin.arch.mode_construction')}
                        >
                            <FaHardHat size={11} />
                            <span className="hidden sm:inline">{t('admin.arch.mode_construction')}</span>
                        </button>
                        <button
                            onClick={() => setMode('arch')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                mode === 'arch' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title={t('admin.arch.mode_arch')}
                        >
                            <FaDraftingCompass size={11} />
                            <span className="hidden sm:inline">{t('admin.arch.mode_arch')}</span>
                        </button>
                    </div>
                    )}
                    <button
                        onClick={handleSaveToDb}
                        disabled={isDbSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-primary rounded-lg border border-primary/80 transition-all hover:bg-primary/90 disabled:opacity-60 shrink-0"
                        title={t('admin.estimator.save_title')}
                    >
                        <FaSync size={12} className={isDbSaving ? 'animate-spin' : ''} />
                        <span className="hidden sm:inline">{isDbSaving ? t('admin.estimator.saving') : t('admin.estimator.save')}</span>
                    </button>
                    <button
                        onClick={handleReset}
                        className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:bg-gray-50 hover:text-gray-600 shrink-0"
                        title={t('admin.estimator.new_estimate')}
                    >
                        <FaPlus size={13} />
                    </button>
                    <button
                        onClick={onLogout}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0"
                        title={t('admin.estimator.sign_out')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>

                {/* Status sub-row */}
                <div className="flex gap-2 items-center px-4 pb-2 mx-auto max-w-7xl text-xs text-gray-400">
                    <span>{estimate.number}</span>
                    <span>·</span>
                    <span>{t('admin.estimator.items_count', { count: estimate.lines.length })}</span>
                    {isDbSaving && (
                        <span className="flex gap-1 items-center text-violet-400">
                            <FaCloudUploadAlt size={9} className="animate-pulse" /> {t('admin.estimator.saving')}
                        </span>
                    )}
                    {!isDbSaving && dbSavedAt && (
                        <span className="flex gap-1 items-center text-emerald-500">
                            <FaCheckCircle size={9} /> {t('admin.estimator.saved_at', { time: dbSavedAt })}
                        </span>
                    )}
                    {!isDbSaving && !dbSavedAt && isSaving && (
                        <span className="flex gap-1 items-center text-gray-400">
                            <FaCloudUploadAlt size={9} /> {t('admin.estimator.auto_draft')}
                        </span>
                    )}
                </div>

                {/* Tab bar */}
                <div className="flex px-4 mx-auto max-w-7xl border-t border-gray-100 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                    {TABS.filter(t2 => t2.id !== 'editor' || roleLoading || canEditCatalog).map(({ id, label, icon: Icon }) => {
                        const TAB_KEY_MAP = { estimados: 'tab_estimados', builder: 'tab_builder', editor: 'tab_editor' }
                        const resolvedLabel = t(`admin.estimator.${TAB_KEY_MAP[id] ?? id}`)
                        return (
                        <button
                        key={id}
                        onClick={() => setTab(id)}
                        className={`flex-1 shrink-0 flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all -mb-px ${
                                tab === id
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon size={13} />
                            {resolvedLabel}
                            {id === 'estimados' && <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] leading-none"><FaListUl size={8} /></span>}
                            {id === 'builder' && estimate.lines.length > 0 && (
                                <span className="px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] leading-none">{estimate.lines.length}</span>
                            )}
                        </button>
                        )
                    })}
                </div>
            </header>

            {/* Body */}
            <main className="px-3 py-5 mx-auto max-w-7xl sm:px-6">
                <AnimatePresence mode="wait">
                    {tab === 'estimados' && (
                        <motion.div key="estimados" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <EstimatesList
                                estimates={estList}
                                loading={estLoading}
                                error={estError}
                                reload={reloadEstimates}
                                onResume={handleResumeFromList}
                                onPreview={est => setPreviewEst(est)}
                                session={session}
                                conSchedulePresets={conSchedulePresets}
                            />
                        </motion.div>
                    )}
                    {tab === 'builder' && (
                        <motion.div key="builder" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <BuilderTab estimate={estimate} onChange={updateEstimate} session={session} conSchedulePresets={conSchedulePresets} onPushToSources={handlePushToSources} isPushing={isPushing} />
                        </motion.div>
                    )}
                    {tab === 'editor' && (
                        <motion.div key="editor" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <EditorTabWrapper
                                canEdit={canEditCatalog}
                                session={session}
                                conSchedulePresets={conSchedulePresets}
                                conSchedulePresetsLoading={conSchedulePresetsLoading}
                                conCreatePreset={conCreatePreset}
                                conUpdatePreset={conUpdatePreset}
                                conDeletePreset={conDeletePreset}
                                conSetDefault={conSetDefault}
                                conClearDefault={conClearDefault}
                                conReloadPresets={conReloadPresets}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {/* Preview overlay */}
            <AnimatePresence>
                {previewEst && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex overflow-hidden fixed inset-0 z-50 flex-col bg-black/60"
                    >
                        <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200 shrink-0">
                            <p className="text-sm font-bold text-gray-800">
                                {t('admin.estimator.preview_title')} — {previewEst.client || previewEst.number}
                            </p>
                            <button
                                onClick={() => setPreviewEst(null)}
                                className="p-2 text-gray-400 rounded-lg transition-all hover:text-gray-700 hover:bg-gray-100"
                            >
                                <FaTimesCircle size={14} />
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-4 bg-gray-100">
                            <EstimatePreview estimate={previewEst} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
        </div>
        <div className={mode !== 'arch' ? 'hidden' : undefined}>
            <ArchEstimateBuilder
                session={session}
                onReturn={onReturn}
                onLogout={onLogout}
                onSwitchToConstruction={() => setMode('construction')}
            />
        </div>
        </>
    )
}
